import { useState, useEffect } from 'react';
import {
    Plus,
    Search,
    Filter,
    Download,
    Upload,
    Edit,
    Trash2,
    ChevronDown,
    ChevronUp,
    RefreshCw,
    AlertCircle,
    CheckCircle,
    XCircle,
    Globe,
    Anchor,
    MapPin
} from 'lucide-react';
import PortImport from '../../components/PortImport';
import api from '../../services/api';

export default function PortManagement() {
    const [ports, setPorts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('all');
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [selectedPort, setSelectedPort] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [stats, setStats] = useState({ total: 0, sea: 0, river: 0, lake: 0 });

    const fetchPorts = async () => {
        setLoading(true);
        try {
            const response = await api.get('/ports', {
                params: {
                    search: searchTerm.length >= 2 ? searchTerm : undefined,
                    limit: 1000 // Get all ports for client-side filtering
                }
            });
            const portData = response.data.data || [];
            setPorts(portData);

            // Calculate statistics
            const stats = {
                total: portData.length,
                sea: portData.filter(p => p.type === 'sea').length,
                river: portData.filter(p => p.type === 'river').length,
                lake: portData.filter(p => p.type === 'lake').length
            };
            setStats(stats);
        } catch (err) {
            console.error('Failed to fetch ports:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPorts();
    }, [searchTerm]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getFilteredAndSortedPorts = () => {
        let filtered = [...ports];

        // Filter by type
        if (selectedType !== 'all') {
            filtered = filtered.filter(port => port.type === selectedType);
        }

        // Client-side search (since we're already doing server-side search)
        if (searchTerm.length >= 2) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(port =>
                port.name.toLowerCase().includes(searchLower) ||
                (port.country && port.country.toLowerCase().includes(searchLower)) ||
                (port.code && port.code.toLowerCase().includes(searchLower))
            );
        }

        // Sort
        filtered.sort((a, b) => {
            const aVal = a[sortField] || '';
            const bVal = b[sortField] || '';
            const comparison = aVal.toString().localeCompare(bVal.toString());
            return sortDirection === 'asc' ? comparison : -comparison;
        });

        return filtered;
    };

    const filteredPorts = getFilteredAndSortedPorts();

    const getTypeBadgeColor = (type) => {
        switch (type) {
            case 'sea': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'river': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
            case 'lake': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'sea': return <Anchor className="w-3 h-3" />;
            case 'river': return <MapPin className="w-3 h-3" />;
            case 'lake': return <Globe className="w-3 h-3" />;
            default: return <MapPin className="w-3 h-3" />;
        }
    };

    const StatCard = ({ label, value, icon: Icon, color }) => (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-500 dark:text-slate-400">{label}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
                </div>
                <div className={`p-3 rounded-lg ${color}`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
            </div>
        </div>
    );

    const SortableHeader = ({ field, label }) => (
        <th
            className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-[#3a663f] dark:hover:text-[#4a7f4f] transition-colors"
            onClick={() => handleSort(field)}
        >
            <div className="flex items-center gap-1">
                {label}
                {sortField === field && (
                    sortDirection === 'asc' ?
                        <ChevronUp className="w-3 h-3" /> :
                        <ChevronDown className="w-3 h-3" />
                )}
            </div>
        </th>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                            <span className="bg-[#3a663f] text-white p-2 rounded-lg">
                                <Anchor className="w-6 h-6" />
                            </span>
                            Port Management
                        </h1>
                        <p className="text-gray-500 dark:text-slate-400 mt-1">
                            Manage and monitor global port infrastructure
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowImportModal(true)}
                            className="inline-flex items-center px-4 py-2 bg-[#3a663f] hover:bg-[#2d5231] text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            Import Ports
                        </button>
                        <button
                            onClick={fetchPorts}
                            className="p-2 text-gray-500 hover:text-[#3a663f] dark:text-slate-400 dark:hover:text-[#4a7f4f] rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                        label="Total Ports"
                        value={stats.total}
                        icon={Globe}
                        color="bg-[#3a663f]"
                    />
                    <StatCard
                        label="Sea Ports"
                        value={stats.sea}
                        icon={Anchor}
                        color="bg-blue-500"
                    />
                    <StatCard
                        label="Air Ports"
                        value={stats.river}
                        icon={MapPin}
                        color="bg-emerald-500"
                    />
                   
                </div>

                {/* Controls Bar */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search ports by name, country, or code..."
                                value={searchTerm}
                                onChange={handleSearch}
                                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3a663f] focus:border-transparent transition-all"
                            />
                        </div>
                        <div className="flex gap-3">
                            <select
                                value={selectedType}
                                onChange={(e) => setSelectedType(e.target.value)}
                                className="px-4 py-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3a663f] focus:border-transparent transition-all"
                            >
                                <option value="all">All Types</option>
                                <option value="sea">Sea</option>
                                <option value="river">River</option>
                                <option value="lake">Lake</option>
                            </select>
                            <button className="px-4 py-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors flex items-center gap-2">
                                <Filter className="w-4 h-4" />
                                <span className="hidden sm:inline">Filter</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Ports Table */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-700/50">
                                <tr>
                                    <SortableHeader field="name" label="Port Name" />
                                    <SortableHeader field="country" label="Country" />
                                    <SortableHeader field="code" label="Code" />
                                    <SortableHeader field="type" label="Type" />
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="px-4 py-12 text-center">
                                            <div className="flex flex-col items-center justify-center">
                                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3a663f]"></div>
                                                <p className="mt-4 text-gray-500 dark:text-slate-400">Loading ports...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredPorts.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-4 py-12 text-center">
                                            <div className="flex flex-col items-center justify-center">
                                                <AlertCircle className="w-12 h-12 text-gray-400 dark:text-slate-500 mb-3" />
                                                <p className="text-gray-500 dark:text-slate-400">No ports found</p>
                                                <button
                                                    onClick={() => setShowImportModal(true)}
                                                    className="mt-4 text-[#3a663f] hover:text-[#2d5231] font-medium transition-colors"
                                                >
                                                    Import ports to get started
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPorts.map((port) => (
                                        <tr
                                            key={port.id}
                                            className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors group"
                                        >
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-shrink-0">
                                                        <div className={`p-2 rounded-lg ${getTypeBadgeColor(port.type)}`}>
                                                            {getTypeIcon(port.type)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900 dark:text-white">
                                                            {port.name}
                                                        </p>
                                                        {port.code && (
                                                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                                                ID: {port.code}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <Globe className="w-4 h-4 text-gray-400" />
                                                    <span className="text-gray-700 dark:text-slate-300">
                                                        {port.country || '-'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <code className="px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded text-xs font-mono text-gray-700 dark:text-slate-300">
                                                    {port.code || '-'}
                                                </code>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(port.type)}`}>
                                                    {getTypeIcon(port.type)}
                                                    {port.type || 'sea'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                                        title="Edit port"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                                        title="Delete port"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer with pagination info */}
                    {!loading && filteredPorts.length > 0 && (
                        <div className="px-4 py-3 bg-gray-50 dark:bg-slate-700/30 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between">
                            <p className="text-sm text-gray-500 dark:text-slate-400">
                                Showing <span className="font-medium">{filteredPorts.length}</span> of{' '}
                                <span className="font-medium">{ports.length}</span> ports
                            </p>
                            <div className="flex items-center gap-2">
                                <button className="px-3 py-1 text-sm bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                    Previous
                                </button>
                                <button className="px-3 py-1 text-sm bg-[#3a663f] text-white rounded hover:bg-[#2d5231] transition-colors">
                                    1
                                </button>
                                <button className="px-3 py-1 text-sm bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors">
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Import Modal */}
            {showImportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                        <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-[#3a663f]/10 rounded-lg">
                                    <Upload className="w-5 h-5 text-[#3a663f]" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Import Ports</h2>
                                    <p className="text-sm text-gray-500 dark:text-slate-400">
                                        Upload an Excel file to import ports
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowImportModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6">
                            <PortImport
                                onImportComplete={() => {
                                    setShowImportModal(false);
                                    fetchPorts();
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}